'use client';

/**
 * Reads `<link rel="alternate" hreflang="...">` for the requested locale.
 *
 * Next.js renders these from page `metadata.alternates.languages`. Product and
 * category metadata builders use the backend slug index (SKU / category code)
 * so the matching href already carries the localized `metaUrl`. Returns the
 * `pathname` of the matched URL (without query string — callers decide what to
 * do with the current `?qs`). Returns `null` when no matching tag is present.
 */
export const getHreflangPathname = (targetLocale: string): string | null => {
  if (typeof document === 'undefined') return null;
  const lc = targetLocale.trim().toLowerCase();
  if (!lc) return null;

  const links = document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]');
  for (const link of Array.from(links)) {
    const hreflang = (link.hreflang || link.getAttribute('hreflang') || '').trim().toLowerCase();
    if (hreflang !== lc) continue;
    const href = link.href || link.getAttribute('href') || '';
    if (!href) continue;
    try {
      return new URL(href, window.location.origin).pathname;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Resolves the destination pathname for a locale switch.
 *
 * Prefers the hreflang alternate that the page already advertises (product /
 * category pages translate `metaUrl` between locales). When no alternate
 * exists for the requested locale — typically a product / category that is
 * not translated — we send the user to the target locale's landing page
 * rather than keep the source slug (which would 404).
 */
export const resolveLocalizedPathname = (targetLocale: string, defaultLocale: string): string => {
  const fromHreflang = getHreflangPathname(targetLocale);
  if (fromHreflang) return fromHreflang;
  const lc = targetLocale.trim().toLowerCase();
  const def = (defaultLocale ?? '').trim().toLowerCase();
  return lc === def || !lc ? '/' : `/${lc}`;
};
